import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FundraisingResponse } from '@shared/schemas';

import type {
  FundraisingDraft,
  FundraisingFieldChange,
} from '../../_shared/domain/fundraising-draft.model';
import type { FundraisingFormError } from '../../_shared/domain/fundraising-form-error';
import { makeInitialState } from './add-fundraising.state';

/**
 * Add-fundraising slice. Reducers are pure state transitions only; every side
 * effect (validation trigger, the create POST) lives in the saga, which
 * dispatches the `save*` transitions below as it progresses.
 */
const slice = createSlice({
  name: 'addFundraising',
  initialState: makeInitialState,
  reducers: {
    /** A fresh form, starting from the given draft (the dialog's or the empty card's defaults). */
    formOpened: (_state, action: PayloadAction<FundraisingDraft>) => ({
      ...makeInitialState(),
      draft: action.payload,
    }),
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (state, action: PayloadAction<FundraisingFieldChange>) => {
      const change = action.payload;
      if (change.field === 'amount') state.draft.amount = change.value;
      else if (change.field === 'currency') state.draft.currency = change.value;
      else if (change.field === 'closeMonth') state.draft.closeMonth = change.value;
      else state.draft.closeYear = change.value;
      delete state.validationErrors[change.field];
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state, action: PayloadAction<FundraisingResponse>) => {
      state.saveStatus = 'succeeded';
      state.created = action.payload;
    },
    saveFailed: (state, action: PayloadAction<FundraisingFormError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to a pristine state (on mount / after success). */
    resetForm: () => makeInitialState(),
  },
});

export const addFundraisingActions = slice.actions;
export const addFundraisingReducer = slice.reducer;
